import React, { useState } from 'react';
import Button from '../Button';
import DocumentForm from '../DocumentForm';
import DocumentsRepeater from './DocumentsRepeater';
import FontAwesomeIcon from '../Icon';
import Overlay from '../Overlay';
import Search from '../Search';
import Empty from '../Empty';
import Actions from '../Actions';
import { useSelector, useDispatch } from 'react-redux';
import { clearFndFormData } from 'src/store/actions/fndForm';
import { toast } from 'react-toastify';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import { selectSearch } from 'src/store/selectors/search';
import ContentBlock from '../ContentBlock';
import { IntroContent, USER_ROLES } from 'src/common/constants';
import { getUrlExtension } from 'src/helpers/CommonHelpers';
import { groupDocuments } from './DocumentGrouping';
import { DocumentGroup } from './DocumentGroup';
import { DocumentFormData } from 'src/utils/S3UploadDocumentsUtility';
import download from 'downloadjs';
import ReusableLightbox from 'src/components/ReusableLightbox/reusable-lightbox';
import 'yet-another-react-lightbox/styles.css';
import './Documents.scss';

interface DocumentsProps {
  documents: any[];
  breadcrumb?: any;
  onSearch: (searchTerm: string) => void;
  onSaveDocument: (document: DocumentFormData) => void;
  onDeleteDocument: (documentId: string) => void;
  labelName: string;
  introContent?: boolean;
  disableGrouping?: boolean;
  from?: string;
}

const DocumentsLegacyRedux = ({
  documents,
  breadcrumb,
  onSearch,
  onSaveDocument,
  onDeleteDocument,
  labelName,
  introContent,
  disableGrouping = false,
  from,
}: DocumentsProps) => {
  const [showOverlay, setShowOverlay] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [selectedImage, setSelectedImage] = useState<string>('');

  const user = useSelector(selectAuthenticatedUser);
  const search = useSelector(selectSearch);
  const dispatch = useDispatch();
  const handleClearFndFormData = () => dispatch(clearFndFormData());

  const onClick = (data: string) => {
    if (data.includes('.mp4')) {
      toast.success(
        `Your ${labelName} download has started, It may take some time.`,
        { autoClose: 12000, position: 'top-center' }
      );
    }
    download(data);
  };

  const onImageClicked = (x: number, url: string, isImage: boolean) => {
    if (isImage) {
      if (getUrlExtension(url) === 'heic')
        return window.open(url, '_blank')?.focus();
      setIsOpen(true);
      setSelectedImage(url);
      return;
    }
    window.open(url, '_blank')?.focus();
  };

  const homeOwnerCheck: boolean =
    user &&
    user.roles &&
    user.roles.length > 0 &&
    (user.roles[0].role.name === USER_ROLES.HOME_OWNER ||
      user.roles[0].role.name === USER_ROLES.OCCUPANT);

  const isSearching: boolean = search && search?.documents?.length > 0;
  const { priority, General, ...groupedDocuments } = groupDocuments(documents);

  return (
    <div className="documents">
      {introContent && (
        <ContentBlock content={IntroContent.manualsAndDocuments} />
      )}
      <Actions compact>
        <Search
          size="small"
          placeholder={`${labelName}...`}
          onChange={onSearch}
          theme={undefined}
        />
        {user.roles[0].role.name === USER_ROLES.STRATA_MANAGER ||
        user.roles[0].role.name === USER_ROLES.VENDOR ||
        user.roles[0].role.name === USER_ROLES.OCCUPANT ||
        user.roles[0].role.name === USER_ROLES.REALTOR ||
        (homeOwnerCheck && localStorage.getItem('theme') === 'Rize') ? null : (
          <Button
            color="teal"
            label={`Add ${labelName}`}
            onClick={() => {
              setShowOverlay(true);
              handleClearFndFormData();
            }}
            icon={
              <FontAwesomeIcon
                glyph="file-contract"
                weight="light"
                size="small"
              />
            }
            size="small"
          />
        )}
      </Actions>
      {documents.length > 0 ? (
        <>
          {isSearching || disableGrouping ? (
            <DocumentsRepeater
              documents={documents}
              onDeleteDocument={onDeleteDocument}
              onDownload={(data: any) => onClick(data)}
              onClick={(x: any, data: any, isImage): any =>
                onImageClicked(x, data, isImage)
              }
              user={user}
              from={from}
            />
          ) : (
            <>
              {/* Document Groups */}
              {priority.length > 0 && (
                <DocumentGroup
                  name="Priority"
                  documents={priority}
                  defaultExpanded
                  onDownload={(data) => onClick(data)}
                  onDeleteDocument={onDeleteDocument}
                  user={user}
                  onClick={(idx, url, isImage) =>
                    onImageClicked(idx, url, isImage)
                  }
                  from={undefined}
                />
              )}
              {Object.keys(groupedDocuments).map((group) => {
                if (groupedDocuments[group].length === 0) return null;
                return (
                  <DocumentGroup
                    key={group}
                    name={group}
                    documents={groupedDocuments[group]}
                    onDownload={(data: any) => onClick(data)}
                    onDeleteDocument={onDeleteDocument}
                    user={user}
                    onClick={(idx: any, url: any, isImage: any) =>
                      onImageClicked(idx, url, isImage)
                    }
                    from={undefined}
                  />
                );
              })}
              {General.length > 0 && (
                <DocumentGroup
                  name="Manuals & Documents "
                  documents={General}
                  onDownload={(data: any) => onClick(data)}
                  onDeleteDocument={onDeleteDocument}
                  user={user}
                  onClick={(idx: any, url: any, isImage: any) =>
                    onImageClicked(idx, url, isImage)
                  }
                  from={undefined}
                />
              )}
            </>
          )}
          {isOpen && (
            <ReusableLightbox
              isOpen={isOpen}
              onClose={() => setIsOpen(false)}
              image={selectedImage}
            />
          )}
        </>
      ) : (
        <Empty
          headline={`No ${labelName} yet`}
          strapline={`All ${labelName} you add will be listed here so it's easier for you to manage.`}
          searchIsActive={search && search.documents ? search.documents : ''}
          cta={
            user.roles[0].role.name === USER_ROLES.STRATA_MANAGER ||
            user.roles[0].role.name === USER_ROLES.HOME_OWNER ||
            user.roles[0].role.name === USER_ROLES.REALTOR ||
            (homeOwnerCheck &&
              localStorage.getItem('theme') === 'Rize') ? null : (
              <div>
                <Button
                  color="teal"
                  label={`Add ${labelName}`}
                  onClick={() => {
                    setShowOverlay(true);
                    handleClearFndFormData();
                  }}
                  icon={
                    <FontAwesomeIcon
                      glyph="file-contract"
                      weight="light"
                      size="small"
                    />
                  }
                  size="small"
                />
              </div>
            )
          }
          image={
            <img
              alt={`No ${labelName} yet`}
              src="/assets/empty/documents.svg"
            />
          }
          direction={undefined}
        />
      )}
      <Overlay
        showOverlay={showOverlay}
        content={
          <DocumentForm
            onSaveDocument={onSaveDocument}
            from={from}
            breadcrumb={breadcrumb}
            user={user}
            onClose={() => {
              setShowOverlay(false);
              handleClearFndFormData();
            }}
          />
        }
      />
    </div>
  );
};

export default DocumentsLegacyRedux;
